<template>
  <div class="tutor-turn" :class="{ grouped: !showIdentity }">
    <!-- Identity row appears once per consecutive run of Arti messages;
         "Arti" is a proper noun and deliberately not translated. -->
    <div v-if="showIdentity" class="tutor-id">
      <img
        src="@/assets/articulearn-logo-a.png"
        class="tutor-id-logo"
        alt=""
      />
      <span class="tutor-id-name">Arti</span>
    </div>
    <div class="tutor-flow">
      <div v-if="isLoading">
        <MessageLoading :phase="phase" />
      </div>
      <div v-else>
        <!-- The reveal is skippable by tap, and while it is running the block
             also takes focus and answers Enter/Space, so a keyboard or
             switch-access reader is not made to wait it out. The tab stop only
             exists while there is something to skip. Once the reveal is over,
             the same two handlers serve the phrases the DOM pass wraps inside
             this block (see "Phrase replay" in the script). -->
        <div
          ref="bubble"
          class="tutor-md"
          :class="{ 'tutor-md-revealing': isRevealing }"
          :style="revealStyle"
          :tabindex="isRevealing ? 0 : undefined"
          :aria-label="isRevealing ? $t('chat.showFullReply') : undefined"
          v-html="renderedMessage"
          @click="onBubbleClick"
          @keydown="onBubbleKeydown"
        ></div>
        <!-- What a live phrase does, read after its name by a screen reader
             through aria-describedby. One per message, only while a phrase is
             live. -->
        <span v-if="phraseHintShown" :id="phraseHintId" class="visually-hidden">{{
          $t("chat.phraseReplayHint")
        }}</span>
        <InteractionRenderer
          v-if="interaction"
          :interaction="interaction"
          :live="interactionLive"
          @submit="$emit('submit-interaction', $event)"
        />
        <!-- While the reply is being spoken segment by segment there is no
             audioId yet, and deliberately no scrubber: AudioPlayer binds its
             whole UI to one audioId and the segment ids are transient, so it
             would desync at every handoff. A learner cannot scrub a live
             speaker mid-sentence either. The full transport appears, paused,
             when the stitched file attaches. -->
        <div
          v-if="isSpeaking"
          class="speaking-indicator"
          role="status"
          aria-live="polite"
        >
          <span class="speaking-dots" aria-hidden="true">
            <span class="speaking-dot"></span>
            <span class="speaking-dot"></span>
            <span class="speaking-dot"></span>
          </span>
          <span class="speaking-label">{{ $t("chat.speaking") }}</span>
        </div>
        <!-- Rendered the moment a stitched file exists, with no extra
             condition. An earlier revision hid it while the session was still
             speaking, to avoid offering a scrubber over audio being spoken live
             - but that made the learner's only way to hear the reply depend on
             the session reaching a terminal state, and a session whose end
             event never arrives would have hidden it for good. -->
        <!-- The audio row is a size container, so the pill can hide its replay
             button when the message column is too narrow to hold it. -->
        <div v-if="audioId" class="tutor-audio">
          <AudioPlayer
            :audioId="audioId"
            :isAutoPlay="autoPlay"
            :dialogId="dialogId"
            :messageKey="messageKey"
            :deferFetch="deferAudioFetch"
            :playbackControls="playbackControls"
            :spanTimings="phraseSpanTimings"
          />
        </div>

        <!-- A pre-authored opener asks its question in the language being
             learned, so the map that comes with it carries an explanation in
             the learner's own language. The disclosure is deliberately the same
             control as the spoken-version one below, down to the chip and the
             revealed box, so a chat never shows two different-looking ways of
             revealing a line of text. It differs in exactly one respect: it can
             start open, because an A1 learner needs the explanation to answer
             the question at all, while a higher band gets it as a safety net. -->
        <div v-if="openerHelpText" class="opener-help-container">
          <button
            class="help-chip"
            @click="showOpenerHelp = !showOpenerHelp"
            type="button"
            :aria-expanded="showOpenerHelp ? 'true' : 'false'"
          >
            <font-awesome-icon
              :icon="showOpenerHelp ? 'chevron-up' : 'chevron-down'"
              class="toggle-icon"
            />
            {{ showOpenerHelp ? $t("chat.hideHelp") : $t("chat.showHelp") }}
          </button>
          <div v-if="showOpenerHelp" class="help-content">
            {{ openerHelpText }}
          </div>
        </div>

        <div v-if="spokenText" class="spoken-text-container">
          <button
            class="spoken-chip"
            @click="showSpokenText = !showSpokenText"
            type="button"
            :aria-expanded="showSpokenText ? 'true' : 'false'"
          >
            <font-awesome-icon
              :icon="showSpokenText ? 'chevron-up' : 'chevron-down'"
              class="toggle-icon"
            />
            {{ showSpokenText ? $t("chat.hideSpokenVersion") : $t("chat.showSpokenVersion") }}
          </button>
          <div v-if="showSpokenText" class="spoken-text-content">
            {{ cleanSpokenText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MessageLoading from "./MessageLoading.vue";
import AudioPlayer from "./AudioPlayer.vue";
import InteractionRenderer from "./interactions/InteractionRenderer.vue";
import { effectivePlaybackRate, playbackRateState } from "@/scripts/playbackRate.js";
import { playbackState } from "@/scripts/audioPlaybackService";
import {
  anySegmentSessionLive,
  playPhraseSlice,
  setPhraseSliceLoop,
  sliceState,
  stopSlicesFor,
} from "@/scripts/slicePlayback.js";
import { clipCacheState } from "@/scripts/clipCache.js";
import { recordingState } from "@/store/recordingState.js";
import { audioMatches } from "@/scripts/phraseMatching.js";
import {
  PLAIN_RENDER_REASONS,
  phraseCandidates,
  renderPhraseMarkup,
  stripPhraseMarkers,
  wrapPhraseMarkers,
} from "@/scripts/phraseMarkers.js";
import { usePlaybackEvents } from "@/composables/usePlaybackEvents.js";

// ---------- Phrase replay ----------
//
// While phraseReplay is on and the message has audio, the learning-language
// spans of its writtenSpans are wrapped in the bubble (src/scripts/
// phraseMarkers.js places markers at their stored offsets before the render
// and wraps what lies between them after it). A wrapped span whose text the
// audio holds (src/scripts/phraseMatching.js) is live: underlined, a button in
// this message's roving focus group, and a tap plays exactly its slice on the
// playback service's second player. Every other wrapped span is inert, a plain
// <span class="phrase"> with no role, no tab stop and no underline.
//
// The bubble renders with markers at most twice (plain before the audio
// attaches on the text-first path, marked after), because renderedMessage
// depends only on the text, the written spans, the flag and whether there is
// audio. Timings, the reveal, the segment phase, the slice player and the pill
// never re-render it: the pass only toggles attributes and classes on the
// pieces it wrapped, so a selection, the tint and the repeat button survive.
//
// The flag is read from the clip cache's `enabled` switch, which
// src/scripts/phraseReplaySync.js keeps equal to featureFlags.phraseReplay
// (the component suites load this file without the flag store).
//
// Spec: docs/superpowers/specs/2026-09-25-audio-playback-speed-and-phrase-replay-design.md,
// section 3.

// The only timings format this client understands. A message written by a
// later server with another version keeps today's pill and inert phrases.
const KNOWN_TIMINGS_VERSION = 1;
// A slice runs past the spoken span's end by this much, so the last sound of
// the phrase is not clipped.
const SLICE_END_PADDING_SECONDS = 0.03;
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
// The repeat glyph: two arrows chasing each other round a loop. Drawn as SVG
// in the DOM pass because the button is inserted into v-html content, where a
// Vue icon component cannot render.
const REPEAT_GLYPH_PATHS = [
  "M17 2l4 4-4 4",
  "M3 11V10a4 4 0 0 1 4-4h14",
  "M7 22l-4-4 4-4",
  "M21 13v1a4 4 0 0 1-4 4H3",
];
let phraseHintSequence = 0;

// The message's spanTimings when they can be trusted: the known version, a
// non-empty list, and every entry a range of finite, ordered, non-negative
// seconds. Anything else is treated as no timings at all.
function usableSpanTimings(replay) {
  if (!replay || replay.timingsVersion !== KNOWN_TIMINGS_VERSION) return null;
  const timings = replay.spanTimings;
  if (!Array.isArray(timings) || !timings.length) return null;
  const valid = timings.every(
    (timing) =>
      timing &&
      typeof timing === "object" &&
      Number.isFinite(timing.start) &&
      Number.isFinite(timing.end) &&
      timing.start >= 0 &&
      timing.end > timing.start &&
      (timing.onset === undefined || Number.isFinite(timing.onset))
  );
  return valid ? timings : null;
}

// The spoken-version disclosure shows the tutor's raw audio script, so every
// tag-shaped token in it has to be removed before it reaches the screen.
//
// Two shapes are stripped, and both mirror what the backend already does.
//
// 1. Language tags. `<lang=xx>...</lang>` chooses the voice for a span. This
//    pattern accepts every code shape the backend's own orphan-tag pattern
//    accepts (audio.py's _ORPHAN_LANGUAGE_TAG_PATTERN), including the
//    region-qualified forms a model drifts into: `<lang=es-MX>`, `<lang=pt_BR>`,
//    `<lang=EN>`. The previous client pattern required exactly two letters, so
//    the synthesizer consumed a region-qualified tag correctly while its
//    characters still leaked into this text.
//
// 2. Model-invented HTML tags. Nothing in any prompt asks for HTML, but PROD
//    message -P-zqMg6Npu7gyN0SrZB stored "...getting the </span><lang=es>gustar
//    </lang>...", where a `</lang>` closer belonged. The name groups carry the
//    same safety asymmetry the backend stripper uses (audio.stripStrayHtmlTags):
//    multi-letter names are stripped in any form, while single-letter names are
//    stripped only when bare, because "<a place>" and "<tu nombre>" are
//    placeholders a tutor legitimately writes and must survive.
//
// Doing this at render time is what repairs messages already stored in RTDB —
// an updated client stops showing the tags with no data migration.
const LANGUAGE_TAG_PATTERN = /<\/?lang(?:=[^<>]{0,32})?>/gi;

const STRAY_TAG_MULTI_LETTER_NAMES = [
  "strong",
  "table",
  "span",
  "div",
  "br",
  "em",
  "ul",
  "ol",
  "li",
  "tr",
  "td",
  "th",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
];
const STRAY_TAG_BARE_ONLY_NAMES = ["a", "b", "i", "p", "u"];
// Bounded attribute tail, matching the backend's STRAY_TAG_ATTRIBUTE_TAIL_MAX.
// The class already excludes "<" and ">", so a match can never run past one
// tag-shaped token; the bound caps how far the greedy quantifier backtracks
// before giving up at a given position.
const STRAY_TAG_ATTRIBUTE_TAIL_MAX = 64;
// Progressive reveal pacing. A tutor speaking aloud covers roughly this many
// characters a second, which is the pace the reveal is meant to match: the text
// should arrive with the voice, not race ahead of it or lag behind it. The
// bounds stop a two-word reply from flashing and stop a long one from masking
// text for an uncomfortable stretch.
const REVEAL_CHARS_PER_SECOND = 15;
const REVEAL_MIN_MS = 600;
const REVEAL_MAX_MS = 12000;

// How late a segment session may appear and still start the reveal. The reply's
// text lands before its first segment does, so there is always a gap; while it
// is short the reveal reads as the text arriving with the voice, but past this
// point the learner has been reading unmasked words and dimming them back down
// would interrupt rather than reveal. A session that shows up later than this
// gets no reveal at all, and the message simply stays fully readable.
//
// The value has to clear the whole first-segment path, not just synthesis: the
// design estimates roughly one short TTS call (about one to two seconds) and
// then the write and its propagation to this client still have to happen. A
// budget set at the synthesis estimate alone would put a large share of real
// turns on the wrong side of the line and leave the reveal quietly never
// running.
const REVEAL_ARRIVAL_GRACE_MS = 4000;

const STRAY_HTML_TAG_PATTERN = new RegExp(
  `</?(?:${STRAY_TAG_MULTI_LETTER_NAMES.join(
    "|"
  )})(?:\\s[^<>]{0,${STRAY_TAG_ATTRIBUTE_TAIL_MAX}})?/?>` +
    `|</?(?:${STRAY_TAG_BARE_ONLY_NAMES.join("|")})>`,
  "gi"
);

export default {
  name: "TutorMessage",
  props: {
    messageKey: String,
    dialogId: String,
    msg: String,
    spokenText: String,
    audioId: String,
    // Server-written (seconds) once the client stamps it. Present => this
    // message was already seen on a prior render, so we never re-stamp it
    // (write-once); this keeps the perceived-latency metric honest.
    seenAt: Number,
    isLoading: { type: Boolean, default: false },
    phase: { type: String, default: "thinking" },
    autoPlay: { type: Boolean, default: false },
    // Set once this client has played the reply as segments, so the stitched
    // player mounts without re-downloading what the learner already heard.
    deferAudioFetch: { type: Boolean, default: false },
    // The phase of this message's segment playback session, or null when this
    // client holds none: "playing", "waiting", "suspended" or "completed" - the
    // last two are both terminal, one by interruption or failure and one by the
    // reply finishing normally. Null is every
    // ordinary message — history, a whole-file reply, another device's turn —
    // and it is what scopes both the speaking indicator and the progressive
    // text reveal to segment-mode turns only.
    segmentPhase: { type: String, default: null },
    // Built once per message by ChatScreen.appendChatLog and patched in place
    // there, never in a template: { playbackControls, dialogType,
    // learningLang }, with the phrase-replay fields added later. Null for a
    // message that did not come through the chat (Daily Play mounts this
    // component without it), which keeps today's pill.
    replay: { type: Object, default: null },
    interaction: Object,
    interactionLive: { type: Boolean, default: false },
    // Written by the backend on a pre-authored first tutor message and absent
    // on every other one. Carries `band`, `responseType`, `contentHash`,
    // `langCode`, `helpOpen`, and - when the learner's native language is one
    // the help table covers - `helpText`. Only the last two are read here.
    opener: Object,
    // False when the previous rendered message is also Arti's, so a run of
    // consecutive tutor messages shows the logo + name once (visual grouping).
    showIdentity: { type: Boolean, default: true },
  },
  emits: ["submit-interaction"],
  components: {
    AudioPlayer,
    MessageLoading,
    InteractionRenderer,
  },
  data() {
    return {
      showSpokenText: false,
      // Seeded from the message rather than defaulted, because the band decides
      // whether the learner needs the explanation to answer at all. Reading a
      // prop in data() fixes this at creation, which is what we want: a later
      // change to the map must not reopen a disclosure the learner just closed.
      // Anything other than an explicit true stays collapsed, so a record
      // written before this flag existed never pushes native-language text into
      // a chat unasked.
      showOpenerHelp: this.opener?.helpOpen === true,
      observer: null,
      hasBeenSeen: false,
      // The progressive reveal runs at most once per message and is never
      // re-armed, so a session ending (or a tap) settles it for good.
      revealStarted: false,
      revealDone: false,
      revealTimer: null,
      // The reveal's timing as it is running. A speed change mid-reveal keeps
      // the progress made so far and re-times the rest at the new rate, so
      // these hold the progress at the last change (a fraction of the whole),
      // when that was, the duration in force since, and the animation delay
      // that keeps the CSS animation at that same progress.
      revealStartedAtMs: 0,
      revealAnchorAtMs: 0,
      revealAnchorFraction: 0,
      revealTotalMs: 0,
      revealDelayMs: 0,
      // When this message first rendered, for the grace window above.
      renderedAtMs: Date.now(),
      // Why this message is shown plain after a failed phrase pass (a lost
      // marker pair or a pass that threw), or null. Once set it stays for the
      // life of this component, so a message that cannot be wrapped is
      // rendered plain rather than retried on every flag flip.
      phrasePlainReason: null,
      // The id of the hidden description every live phrase points at.
      phraseHintId: `phrase-hint-${++phraseHintSequence}`,
      // Whether any phrase currently has a live control in the DOM, which is
      // when the hidden description is rendered. Written by the DOM pass,
      // because a span can be live by its audio and still have no control
      // (inside a link, or on a message that fell back to plain).
      phraseHintShown: false,
    };
  },
  computed: {
    // The explanation to show, or the empty string when there is none to show.
    // Three separate cases land here and all of them mean "no disclosure": the
    // B2_C1 task ships with no help text at all, a learner whose native language
    // the help table does not cover gets a map with the field omitted, and every
    // field on this map is read back from RTDB, so a blank string is possible
    // too.
    openerHelpText() {
      const helpText = this.opener?.helpText;
      if (typeof helpText !== "string") return "";
      return helpText.trim() === "" ? "" : helpText;
    },
    // Arti is mid-reply: either a segment is playing or the session is waiting
    // for the next one to be synthesized. The gap is part of the same reply, so
    // the indicator must not flicker off inside it.
    isSpeaking() {
      return this.segmentPhase === "playing" || this.segmentPhase === "waiting";
    },
    isRevealing() {
      return this.revealStarted && !this.revealDone;
    },
    // The speed chip on this message's pill, which the chat switches on only
    // for a tutor message in a lesson or a freestyle chat.
    playbackControls() {
      return !!(this.replay && this.replay.playbackControls === true);
    },
    // The rate this message's voice plays at: the effective rate when its
    // audio follows the chip, and 1 otherwise.
    voiceRate() {
      return this.playbackControls ? effectivePlaybackRate.value : 1;
    },
    // Divided by the rate after the clamp, so at 0.5x the text keeps pace
    // with a voice that takes twice as long, including on a long reply that
    // hit the 12 second ceiling.
    revealDurationMs() {
      const length = (this.msg || "").length;
      const clamped = Math.min(
        REVEAL_MAX_MS,
        Math.max(REVEAL_MIN_MS, (length / REVEAL_CHARS_PER_SECOND) * 1000)
      );
      return Math.round(clamped / this.voiceRate);
    },
    // The pace is per message, so it has to reach the stylesheet as a value
    // rather than a class. One animation covers the whole reveal; there is no
    // per-word timer.
    revealStyle() {
      if (!this.isRevealing) return null;
      const style = { "--al-reveal-duration": `${this.revealTotalMs}ms` };
      if (this.revealDelayMs) style["--al-reveal-delay"] = `${this.revealDelayMs}ms`;
      return style;
    },
    // The client's copy of the phraseReplay flag.
    phraseReplayOn() {
      return clipCacheState.enabled === true;
    },
    // The written spans to wrap: only while the flag is on, only once the
    // message has audio (or, in stage two, a tail) to pair them with, and never
    // after a failed pass. An unflagged learner's bubble is rendered plain.
    phraseCandidateList() {
      if (!this.phraseReplayOn || !this.msg || !this.replay) return [];
      // The platform can play a phrase slice: always on web and Android, and
      // on iOS only once the plugin reports rate and range support, so a
      // build with the old plugin never shows a phrase it cannot play.
      if (!playbackRateState.platformSupported) return [];
      if (!this.audioId && !this.replay.tailAudioId) return [];
      if (this.phrasePlainReason) return [];
      return phraseCandidates(this.msg, this.replay.writtenSpans, this.replay.learningLang);
    },
    // The render: plain, or with the markers in place when the marked render
    // matches the plain one once the markers are removed. The table repair
    // and the markdown-it configuration live in src/scripts/tutorMarkdown.js.
    phraseMarkup() {
      if (!this.msg) return { html: "", marked: false, fallback: null };
      return renderPhraseMarkup(this.msg, this.phraseCandidateList);
    },
    renderedMessage() {
      return this.phraseMarkup.html;
    },
    // The timings the phrases and the pill use: none while the flag is off or
    // for a version this client does not know, which keeps today's pill.
    // Also none on an iOS build whose plugin cannot play a phrase range, so
    // such a build keeps today's pill as well as plain text.
    phraseSpanTimings() {
      if (!this.phraseReplayOn || !playbackRateState.platformSupported) return null;
      return usableSpanTimings(this.replay);
    },
    // For each wrapped span, the slice its tap plays and the spoken range it
    // tints over, or null when the audio holds no match for it (it stays
    // inert). A slice runs from the spoken span's start plus its onset,
    // clamped to half the span, to its end plus 30 ms; the playback service
    // clamps the end to the player's reported duration.
    phraseRanges() {
      const candidates = this.phraseCandidateList;
      const timings = this.phraseSpanTimings;
      if (!candidates.length || !timings) return candidates.map(() => null);
      const matches = audioMatches(this.replay.writtenSpans, timings, this.replay.learningLang);
      return candidates.map((candidate) => {
        const match = matches[candidate.spanIndex];
        if (!match || match.spokenIndex === null) return null;
        const spoken = timings[match.spokenIndex];
        const length = spoken.end - spoken.start;
        const onset = Math.min(Math.max(Number(spoken.onset) || 0, 0), length / 2);
        return {
          start: spoken.start + onset,
          end: spoken.end + SLICE_END_PADDING_SECONDS,
          spokenStart: spoken.start,
          spokenEnd: spoken.end,
        };
      });
    },
    // Phrases are inert while the reveal runs (a tap then skips it, as it
    // always has), while this reply is being spoken as segments (the timings
    // describe the stitched clip), and while any reply in the chat is being
    // spoken as segments, because a slice would end that session and cut the
    // rest of the reply short.
    phrasesInteractive() {
      return (
        this.phraseReplayOn &&
        !!this.audioId &&
        !this.isRevealing &&
        !this.isSpeaking &&
        !anySegmentSessionLive()
      );
    },
    phraseLiveStates() {
      return this.phraseRanges.map((range) => this.phrasesInteractive && !!range);
    },
    phraseLiveKey() {
      return this.phraseLiveStates.map((live) => (live ? 1 : 0)).join("");
    },

    // The wrapped span whose slice is loading or sounding, as an index into
    // the wrapped spans, or -1 when the slice player is not playing one of
    // this message's phrases.
    ownSliceIndex() {
      if (sliceState.status === "idle") return -1;
      const owner = sliceState.owner;
      if (!owner || owner.dialogId !== this.dialogId || owner.messageKey !== this.messageKey) {
        return -1;
      }
      return this.phraseCandidateList.findIndex(
        (candidate) => candidate.spanIndex === owner.spanIndex
      );
    },
    // Which phrases are tinted and which is loading, as a string so the
    // watcher that paints them fires only when that changes. A phrase tints
    // while the pill plays through its spoken range, and while its own slice
    // sounds.
    phraseActivityKey() {
      const active = [];
      const pillSpeaking =
        !!this.audioId &&
        playbackState.currentAudioId === this.audioId &&
        playbackState.isPlaying;
      if (pillSpeaking) {
        const time = playbackState.currentTime;
        this.phraseRanges.forEach((range, index) => {
          if (range && time >= range.spokenStart && time < range.spokenEnd) active.push(index);
        });
      }
      let loading = -1;
      const own = this.ownSliceIndex;
      if (own >= 0) {
        if (sliceState.status === "loading") loading = own;
        else if (!active.includes(own)) active.push(own);
      }
      return `${active.join(",")}|${loading}`;
    },
    phraseRepeatKey() {
      return this.ownSliceIndex >= 0 ? `${this.ownSliceIndex}|${sliceState.loop}` : "";
    },
    cleanSpokenText() {
      if (!this.spokenText) return "";
      // Early-out on the overwhelmingly common case: a script with no angle
      // bracket anywhere costs one substring scan and no regex work.
      if (!this.spokenText.includes("<")) return this.spokenText;
      return this.spokenText
        .replace(LANGUAGE_TAG_PATTERN, "")
        .replace(STRAY_HTML_TAG_PATTERN, "");
    },
  },
  methods: {
    // Scoped deliberately to segment-mode turns: a message this client holds a
    // playback session for. This component renders EVERY tutor message through
    // the same path, including history, so an unscoped reveal would change what
    // every user sees with nothing gating it.
    maybeStartReveal() {
      if (this.revealStarted || this.revealDone) return;
      // The speaking phases only. A session can be created ALREADY suspended -
      // the learner tapped record before the first segment was written - and
      // masking the reply then would dim text they can read while nothing is
      // being spoken, which is the opposite of what the reveal is for.
      if (!this.isSpeaking) return;
      // Too late to be a reveal: the learner has had the whole reply in front
      // of them for a while, and masking it now would take words away.
      if (Date.now() - this.renderedAtMs > REVEAL_ARRIVAL_GRACE_MS) {
        this.revealDone = true;
        return;
      }
      this.revealStarted = true;
      const now = Date.now();
      this.revealStartedAtMs = now;
      this.revealAnchorAtMs = now;
      this.revealAnchorFraction = 0;
      this.revealTotalMs = this.revealDurationMs;
      this.revealDelayMs = 0;
      this.armRevealTimer(this.revealTotalMs);
    },
    // The CSS animation is the visual; this timer is the authority, so the
    // mask is guaranteed to come off even where the animation never runs
    // (reduced motion, a browser without mask support).
    armRevealTimer(remainingMs) {
      this.clearRevealTimer();
      this.revealTimer = setTimeout(() => {
        this.revealTimer = null;
        this.revealDone = true;
      }, Math.max(0, remainingMs));
    },
    // The speed changed while the reveal runs. The fraction already revealed
    // stays revealed and the rest is timed at the new rate. The CSS animation
    // keeps its original start, so it is given the delay that puts it at the
    // same fraction under the new duration: progress = (elapsed - delay) /
    // duration, solved for the delay.
    retimeReveal() {
      if (!this.isRevealing || !this.revealTotalMs) return;
      const now = Date.now();
      const fraction = Math.min(
        1,
        this.revealAnchorFraction + (now - this.revealAnchorAtMs) / this.revealTotalMs
      );
      const total = this.revealDurationMs;
      this.revealAnchorFraction = fraction;
      this.revealAnchorAtMs = now;
      this.revealTotalMs = total;
      this.revealDelayMs = Math.round(now - this.revealStartedAtMs - fraction * total);
      this.armRevealTimer((1 - fraction) * total);
    },
    // Deliberately not a `.prevent` modifier on the template. This block holds
    // rendered markdown, so a link inside it can have focus, and an
    // unconditional prevent would swallow Enter on that link -- in history and
    // whole-file replies too, where there is no reveal to skip at all.
    onRevealKeydown(event) {
      if (!this.isRevealing) return;
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") {
        return;
      }
      event.preventDefault();
      this.revealAll();
    },
    // The spec's requirement that the full text stay reachable by tap. It is
    // also the escape hatch if the pacing ever feels slow.
    revealAll() {
      if (!this.isRevealing) return;
      this.clearRevealTimer();
      this.revealDone = true;
    },
    clearRevealTimer() {
      if (!this.revealTimer) return;
      clearTimeout(this.revealTimer);
      this.revealTimer = null;
    },
    // The one click handler on the bubble. While the reveal runs, a tap
    // anywhere on the text skips it, as it always has. After that, a tap on a
    // live phrase plays it and a tap on the repeat button toggles its loop.
    // Nothing inside a link is ever handled here, so a tap can never both play
    // and navigate.
    onBubbleClick(event) {
      if (this.isRevealing) {
        this.revealAll();
        return;
      }
      const piece = this.phraseTargetOf(event, ".phrase-repeat, .phrase");
      if (!piece) return;
      if (piece.classList.contains("phrase-repeat")) {
        this.toggleRepeat();
        return;
      }
      // A mouse drag that selects text inside a phrase still ends in a click;
      // the learner is selecting words to copy, not asking to hear them.
      if (this.clickEndsSelection()) return;
      this.playPhrase(Number(piece.getAttribute("data-span")));
    },
    // The one keydown handler on the bubble: the reveal's Enter and Space
    // while it runs, then Enter and Space on a live phrase and the arrow keys
    // that move between phrases. The repeat button is a real button, so its
    // own Enter and Space arrive as a click.
    onBubbleKeydown(event) {
      if (this.isRevealing) {
        this.onRevealKeydown(event);
        return;
      }
      const piece = this.phraseTargetOf(event, ".phrase-live");
      if (!piece) return;
      const index = Number(piece.getAttribute("data-span"));
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        event.preventDefault();
        this.playPhrase(index);
        return;
      }
      const steps = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (steps[event.key]) {
        event.preventDefault();
        this.moveRovingFocus(index, steps[event.key]);
      }
    },
    // Whether the click is the end of a text selection made in this bubble.
    clickEndsSelection() {
      const root = this.$refs.bubble;
      const selection = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;
      if (!root || !selection || selection.isCollapsed) return false;
      return !!selection.anchorNode && root.contains(selection.anchorNode);
    },
    // The element matching `selector` that an event came from, when it lies
    // inside this bubble and outside any link, or null.
    phraseTargetOf(event, selector) {
      const root = this.$refs.bubble;
      const target = event && event.target;
      if (!root || !target || typeof target.closest !== "function") return null;
      if (target.closest("a")) return null;
      const element = target.closest(selector);
      return element && root.contains(element) ? element : null;
    },
    // Plays one wrapped span's slice. Every guard is checked again here, at
    // the tap, rather than trusted from the last render: the flag (so turning
    // it off in the admin panel takes effect in an open chat at once), the
    // phrase being live, a recording in progress, and a reply being spoken as
    // segments anywhere in the chat.
    playPhrase(index) {
      if (!Number.isInteger(index) || index < 0) return;
      if (!this.phraseReplayOn || !this.phraseLiveStates[index]) return;
      if (recordingState.takeInProgress || anySegmentSessionLive()) return;
      const range = this.phraseRanges[index];
      const candidate = this.phraseCandidateList[index];
      if (!range || !candidate || !this.audioId) return;
      // Synchronously, inside the tap: the service primes its slice player
      // before its first await.
      playPhraseSlice({
        audioId: this.audioId,
        start: range.start,
        end: range.end,
        loop: false,
        owner: {
          dialogId: this.dialogId,
          messageKey: this.messageKey,
          spanIndex: candidate.spanIndex,
        },
      }).catch((error) => console.error("Phrase slice failed to play:", error));
      usePlaybackEvents().trackPhraseTapped({ kind: "spoken" });
      // The phrase the learner activated becomes the group's tab stop.
      this.phraseRovingIndex = index;
      this.syncPhraseLive();
    },
    // The repeat button beside a playing phrase: the first tap loops the
    // phrase, the next lets it finish and stop.
    toggleRepeat() {
      if (this.ownSliceIndex < 0 || !this.phraseReplayOn) return;
      if (sliceState.loop) {
        setPhraseSliceLoop(false);
        return;
      }
      if (recordingState.takeInProgress) return;
      setPhraseSliceLoop(true);
      usePlaybackEvents().trackPhraseLooped({ kind: "spoken" });
    },
    // Whether a wrapped span is live and has a control in the DOM. A span
    // inside a link can be live by its audio while the pass gave it no
    // pieces, and it must never hold the tab stop or be stepped onto.
    phraseHasControl(index) {
      if (!this.phraseLiveStates[index] || !this.phraseDom) return false;
      const pieces = this.phraseDom.pieces.get(index);
      return !!(pieces && pieces.length);
    },
    moveRovingFocus(fromIndex, step) {
      const count = this.phraseLiveStates.length;
      let next = fromIndex + step;
      while (next >= 0 && next < count && !this.phraseHasControl(next)) next += step;
      if (next < 0 || next >= count) return;
      this.phraseRovingIndex = next;
      this.syncPhraseLive();
      const pieces = this.phraseDom && this.phraseDom.pieces.get(next);
      if (pieces && pieces.length) pieces[0].focus();
    },
    // The DOM pass, run after every render of the bubble's HTML (and only
    // then): wraps the marked render's spans and removes its markers, in the
    // same flush as the render, so no marker is ever painted. A pass that
    // finds a marker pair missing, or throws, strips any marker it left and
    // shows the message plain, and the fallback is counted.
    runPhrasePass() {
      const root = this.$refs.bubble;
      // The pass has already wrapped exactly this render (it can be asked
      // twice for one render, from mounted or the loading watcher and from the
      // render watcher). The markers are gone, so a second walk would find
      // none and wrongly fall back to plain.
      if (root && this.phraseDom && this.phraseDom.root === root) {
        if (this.phraseDom.html === this.renderedMessage) return;
      }
      this.phraseDom = null;
      this.phraseRepeatButton = null;
      this.phraseHintShown = false;
      if (!root || !this.phraseMarkup.marked) return;
      const candidates = this.phraseCandidateList;
      let result;
      try {
        result = wrapPhraseMarkers(root, candidates.length);
        if (result.ok) {
          this.phraseDom = {
            root,
            candidates,
            pieces: result.pieces,
            html: this.renderedMessage,
          };
          this.syncPhraseLive();
          this.syncPhraseActivity();
          this.syncRepeatButton();
        }
      } catch (error) {
        console.error("Phrase replay: the wrapping pass failed; showing the message plain", error);
        this.phraseDom = null;
        result = { ok: false, reason: PLAIN_RENDER_REASONS.PASS_ERROR };
      }
      if (result.ok) return;
      try {
        stripPhraseMarkers(root);
      } catch (error) {
        // The plain re-render below replaces the whole block anyway.
      }
      this.phrasePlainReason = result.reason;
      this.countPlainRender(result.reason);
    },
    // Counts each reason once for this message. Created on first use,
    // because the immediate fallback watcher can run before created().
    countPlainRender(reason) {
      if (!this.phraseFallbacksCounted) this.phraseFallbacksCounted = new Set();
      if (this.phraseFallbacksCounted.has(reason)) return;
      this.phraseFallbacksCounted.add(reason);
      usePlaybackEvents().trackPlainRenderFallback({ reason });
    },
    // Makes each wrapped span live or inert without re-rendering. A live span
    // is underlined on every piece; its first piece is the button, named by
    // the span's whole stored text and described by the hidden hint, and its
    // later pieces are hidden from assistive technology so the phrase is read
    // once. One live phrase per message holds the tab stop.
    syncPhraseLive() {
      const dom = this.phraseDom;
      if (!dom) return;
      const live = this.phraseLiveStates;
      let roving = this.phraseRovingIndex;
      if (!Number.isInteger(roving) || !this.phraseHasControl(roving)) {
        roving = live.findIndex((isLive, index) => this.phraseHasControl(index));
      }
      // With no live control (the phrases went inert while a reply is spoken)
      // the learner's place in the group is kept for when they come back.
      if (roving >= 0) this.phraseRovingIndex = roving;
      this.phraseHintShown = roving >= 0;
      dom.candidates.forEach((candidate, index) => {
        const pieces = dom.pieces.get(index) || [];
        const isLive = !!live[index];
        pieces.forEach((piece, pieceIndex) => {
          piece.classList.toggle("phrase-live", isLive);
          if (pieceIndex === 0) {
            if (isLive) {
              piece.setAttribute("role", "button");
              piece.setAttribute("tabindex", index === roving ? "0" : "-1");
              piece.setAttribute("aria-label", candidate.text);
              piece.setAttribute("aria-describedby", this.phraseHintId);
            } else {
              ["role", "tabindex", "aria-label", "aria-describedby"].forEach((name) =>
                piece.removeAttribute(name)
              );
            }
          } else if (isLive) {
            piece.setAttribute("aria-hidden", "true");
          } else {
            piece.removeAttribute("aria-hidden");
          }
        });
      });
    },
    // Paints the tint and the loading state from phraseActivityKey.
    syncPhraseActivity() {
      const dom = this.phraseDom;
      if (!dom) return;
      const [activePart, loadingPart] = this.phraseActivityKey.split("|");
      const active = new Set(activePart ? activePart.split(",").map(Number) : []);
      const loading = Number(loadingPart);
      dom.pieces.forEach((pieces, index) => {
        pieces.forEach((piece, pieceIndex) => {
          piece.classList.toggle("phrase-active", active.has(index));
          piece.classList.toggle("phrase-loading", loading === index);
          if (pieceIndex === 0) {
            if (loading === index) piece.setAttribute("aria-busy", "true");
            else piece.removeAttribute("aria-busy");
          }
        });
      });
    },
    // The repeat button trails this message's phrase while its slice loads or
    // sounds, and goes away when the slice ends. It is a real button with a
    // 44 px target and a label from the locale bundle.
    syncRepeatButton() {
      const dom = this.phraseDom;
      let button = this.phraseRepeatButton;
      // A button the last render replaced is already gone with the old HTML.
      if (button && (!dom || !dom.root.contains(button))) button = null;
      const index = this.ownSliceIndex;
      const pieces = dom && index >= 0 ? dom.pieces.get(index) : null;
      if (!pieces || !pieces.length) {
        if (button) this.removeRepeatButton(button);
        this.phraseRepeatButton = null;
        return;
      }
      if (!button || button.getAttribute("data-span") !== String(index)) {
        if (button) this.removeRepeatButton(button);
        button = this.createRepeatButton(dom.root.ownerDocument, index);
        pieces[pieces.length - 1].after(button);
      }
      button.setAttribute("aria-pressed", sliceState.loop ? "true" : "false");
      this.phraseRepeatButton = button;
    },
    // Removes the repeat button. A keyboard user who had it focused is handed
    // back to its phrase, rather than dropped to the top of the page.
    removeRepeatButton(button) {
      const hadFocus = button.ownerDocument.activeElement === button;
      const index = Number(button.getAttribute("data-span"));
      button.remove();
      if (!hadFocus || !this.phraseDom) return;
      const pieces = this.phraseDom.pieces.get(index);
      const first = pieces && pieces[0];
      if (!first || !first.hasAttribute("tabindex")) return;
      this.phraseRovingIndex = index;
      this.syncPhraseLive();
      first.focus();
    },
    createRepeatButton(doc, index) {
      const button = doc.createElement("button");
      button.setAttribute("type", "button");
      button.className = "phrase-repeat";
      button.setAttribute("data-span", String(index));
      button.setAttribute("aria-label", this.$t("chat.phraseRepeat"));
      const glyph = doc.createElementNS(SVG_NAMESPACE, "svg");
      glyph.setAttribute("viewBox", "0 0 24 24");
      glyph.setAttribute("aria-hidden", "true");
      glyph.setAttribute("focusable", "false");
      REPEAT_GLYPH_PATHS.forEach((d) => {
        const path = doc.createElementNS(SVG_NAMESPACE, "path");
        path.setAttribute("d", d);
        glyph.appendChild(path);
      });
      button.appendChild(glyph);
      return button;
    },
    setupIntersectionObserver() {
      // Already seen on a prior render (seenAt is write-once): don't observe,
      // so re-opening a chat never re-stamps the field.
      if (this.seenAt) return;

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1  // 10% visible - works better for large messages on mobile
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.hasBeenSeen) {
            this.markMessageAsSeen();
          }
        });
      }, options);

      if (this.$el) {
        this.observer.observe(this.$el);
      }
    },

    async markMessageAsSeen() {
      if (this.hasBeenSeen || this.seenAt) return;
      this.hasBeenSeen = true;

      try {
        const { getDatabase, ref, update } = await import('firebase/database');
        const db = getDatabase();
        const messageRef = ref(db, `messages/${this.dialogId}/${this.messageKey}`);

        await update(messageRef, {
          seenAt: Math.floor(Date.now() / 1000)
        });

      } catch (error) {
        console.error('Failed to mark message as seen:', error);
      }
    }
  },
  // created, not mounted: a message that arrives already in segment mode must
  // render masked on its FIRST paint, and anything started in mounted() only
  // takes effect on the following tick, which shows the whole reply for a frame.
  created() {
    // Not reactive: the pass's own bookkeeping, which nothing renders from.
    this.phraseDom = null;
    this.phraseRepeatButton = null;
    this.phraseRovingIndex = null;
    this.maybeStartReveal();
  },
  mounted() {
    this.runPhrasePass();
    if (!this.isLoading && this.messageKey && this.dialogId) {
      this.setupIntersectionObserver();
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.clearRevealTimer();
    // A phrase slice or loop on this message must not outlive the button that
    // stops it. Only this message's own slice is stopped.
    if (this.dialogId && this.messageKey) {
      stopSlicesFor({ dialogId: this.dialogId, messageKey: this.messageKey });
    }
    this.phraseDom = null;
    this.phraseRepeatButton = null;
  },
  watch: {
    // Post-flush, so the pass runs on the DOM the render just wrote and before
    // the browser paints it.
    renderedMessage: {
      handler() {
        this.runPhrasePass();
      },
      flush: "post",
    },
    phraseLiveKey: {
      handler() {
        this.syncPhraseLive();
      },
      flush: "post",
    },
    phraseActivityKey: {
      handler() {
        this.syncPhraseActivity();
      },
      flush: "post",
    },
    phraseRepeatKey: {
      handler() {
        this.syncRepeatButton();
      },
      flush: "post",
    },
    // A render that fell back to plain before any DOM pass (the marked render
    // differed, or the text held a marker character) is counted here.
    "phraseMarkup.fallback": {
      handler(reason) {
        if (reason) this.countPlainRender(reason);
      },
      immediate: true,
    },
    // Turning the flag off re-renders the bubble plain (renderedMessage
    // depends on it), which is how the phrases are unwrapped, and ends any
    // phrase or loop of this message that is sounding.
    phraseReplayOn(on) {
      if (!on && this.dialogId && this.messageKey) {
        stopSlicesFor({ dialogId: this.dialogId, messageKey: this.messageKey });
      }
    },
    // segments/0 usually lands after this message is already on screen, and
    // Vue patches it in place rather than remounting, so mounted() alone would
    // miss the start.
    voiceRate() {
      this.retimeReveal();
    },
    segmentPhase() {
      this.maybeStartReveal();
      // The voice stopped for good, by either terminal phase - "completed" is
      // the ordinary end of a segmented reply, not an edge case. Finishing the
      // reveal rather than letting its own timer run out hands the learner the
      // whole reply at the moment there is nothing left to keep pace with; the
      // timer is paced from the raw markdown, so it can otherwise over-run the
      // speech by seconds on a reply carrying a long link.
      if (this.segmentPhase === "suspended" || this.segmentPhase === "completed") {
        this.revealAll();
      }
    },
    isLoading(newVal) {
      if (!newVal && !this.observer && this.messageKey && this.dialogId) {
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }
      // The bubble appears when loading ends, whether or not its HTML changed.
      if (!newVal) this.$nextTick(() => this.runPhrasePass());
    }
  }
};
</script>

<style scoped>
.tutor-turn {
  margin-bottom: 24px;
  text-align: left;
}

/* Grouped follow-up in a run of Arti messages: pull up against the previous
   turn's 24px bottom margin so the run reads as one breath. */
.tutor-turn.grouped {
  margin-top: -12px;
}

@media (prefers-reduced-motion: no-preference) {
  .tutor-turn {
    animation: msg-enter var(--al-dur-short) var(--al-ease-enter);
  }
}

@keyframes msg-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

.tutor-id {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

/* White chip in both themes: the logo is the brand mark for Arti and matches
   the white header chip, so it stays hue-fixed rather than theme-mapped. */
.tutor-id-logo {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--al-border);
  background-color: #fff;
  object-fit: contain;
}

.tutor-id-name {
  font-size: 12.5px;
  font-weight: 650;
  letter-spacing: 0.02em;
  color: var(--al-text-muted);
}

/* Indent aligns content with the name label (24px logo + 8px gap); 680px is
   the DESIGN.md editorial reading measure so desktop lines stay readable. */
.tutor-flow {
  padding-left: 32px;
  max-width: 680px;
}

/* --- Message-scoped markdown type scale --- */

/* h1/h2 render as section labels, not page headings. --md-label needs ~4.5:1
   AA on the page tint: raw --al-primary is ~3.1:1 on light, so mix it toward
   ink; dark mode's primary on navy is ~5.2:1 and passes as-is. */
.tutor-md {
  --md-label: color-mix(in srgb, var(--al-primary) 62%, var(--al-ink-900));
}

:root[data-theme="dark"] .tutor-md {
  --md-label: var(--al-primary);
}

.tutor-md :deep(p),
.tutor-md :deep(li) {
  font-size: 15px;
  line-height: 1.55;
}

.tutor-md :deep(p) {
  margin: 0 0 10px;
}

.tutor-md :deep(p:last-child) {
  margin-bottom: 0;
}

.tutor-md :deep(ul),
.tutor-md :deep(ol) {
  padding-left: 1.4rem;
  margin: 0 0 10px;
}

.tutor-md :deep(h1),
.tutor-md :deep(h2) {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--md-label);
  margin: 14px 0 8px;
}

.tutor-md :deep(h1)::after,
.tutor-md :deep(h2)::after {
  content: "";
  flex: 1;
  height: 1px;
  background-color: var(--al-border);
  opacity: 0.6;
}

.tutor-md :deep(h3),
.tutor-md :deep(h4),
.tutor-md :deep(h5),
.tutor-md :deep(h6) {
  font-size: 15px;
  font-weight: 650;
  color: var(--al-text);
  margin: 12px 0 6px;
}

.tutor-md :deep(a) {
  color: var(--md-label);
  text-decoration: underline;
}

/* Tables arrive as <div class="md-table"><table>…</table></div> via the
   markdown-it render rule above. Card look; horizontal hairlines only. */
.tutor-md :deep(.md-table) {
  border: 1px solid var(--al-border);
  border-radius: 12px;
  background-color: var(--al-surface-raised);
  overflow: hidden;
  overflow-x: auto;
  margin: 4px 0 10px;
}

.tutor-md :deep(.md-table table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  margin: 0;
}

.tutor-md :deep(.md-table th) {
  text-align: left;
  font-size: 10.5px;
  font-weight: 650;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--al-text-muted);
  background-color: var(--al-surface-sunken);
  padding: 8px 12px;
  border-bottom: 1px solid var(--al-border);
  white-space: nowrap;
}

.tutor-md :deep(.md-table td) {
  padding: 9px 12px;
  border-bottom: 1px solid color-mix(in srgb, var(--al-border) 55%, transparent);
  vertical-align: top;
}

.tutor-md :deep(.md-table tr:last-child td) {
  border-bottom: none;
}

/* Vocabulary emphasis: the first column is the term being taught. */
.tutor-md :deep(.md-table td:first-child) {
  font-weight: 600;
}

/* Blockquotes read as "example sentence" cards. */
.tutor-md :deep(blockquote) {
  background-color: var(--al-primary-soft);
  border-left: 3px solid var(--al-primary);
  border-radius: 12px;
  padding: 10px 14px;
  margin: 4px 0 10px;
  color: var(--al-text);
}

.tutor-md :deep(blockquote p:last-child) {
  margin-bottom: 0;
}

/* Text-relative fill so code stays visible in both themes (a black rgba
   melts into the dark surface). */
.tutor-md :deep(code) {
  background-color: color-mix(in srgb, var(--al-text) 8%, transparent);
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  font-family: "Geist Mono", ui-monospace, SFMono-Regular, monospace;
  font-size: 0.9em;
}

.tutor-md :deep(pre) {
  background-color: color-mix(in srgb, var(--al-text) 8%, transparent);
  padding: 0.75rem;
  border-radius: 8px;
  overflow-x: auto;
  white-space: pre-wrap;
  margin: 0 0 10px;
}

.tutor-md :deep(pre code) {
  background: none;
  padding: 0;
}

.tutor-md :deep(hr) {
  border: none;
  height: 1px;
  background-color: var(--al-border);
  opacity: 0.6;
  margin: 12px 0;
}

/* The pill's row. A block that fills the message column and establishes an
   inline-size container, which AudioPlayer's container query reads to hide
   the replay button below 290px. Its width comes from the column, never from
   the pill, so the containment cannot collapse it. */
.tutor-audio {
  container-type: inline-size;
}

/* --- Phrase replay ---

   The pieces and the repeat button are inserted into the v-html block by the
   DOM pass, so they carry no scoped attribute and are reached with :deep(). */

/* A live phrase: a dotted underline in the primary colour. Its hit height is
   the line box (about 23px at 15px/1.55), a stated exception to DESIGN.md's
   44px minimum, because a taller inline target would break the text layout.
   It stays an inline span, so the text still wraps and a long press still
   selects it. */
.tutor-md :deep(.phrase-live) {
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-decoration-thickness: 1.5px;
  text-decoration-color: color-mix(in srgb, var(--al-primary) 75%, transparent);
  text-underline-offset: 3px;
  border-radius: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
}

.tutor-md :deep(.phrase-live:focus-visible) {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

/* The phrase being spoken, by the pill or by its own slice. */
.tutor-md :deep(.phrase-active) {
  background-color: var(--al-primary-soft);
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
}

/* A tapped phrase whose bytes are still on their way. */
.tutor-md :deep(.phrase-loading) {
  background-color: color-mix(in srgb, var(--al-primary-soft) 55%, transparent);
  cursor: progress;
}

@media (prefers-reduced-motion: no-preference) {
  .tutor-md :deep(.phrase-live) {
    transition: background-color var(--al-dur-short) var(--al-ease-move);
  }
}

/* The repeat button that trails a playing phrase. It draws at 24px so the line
   does not grow, and its 44px target comes from the invisible ::before layer,
   the way the pill's speed chip gets its target. Pressed means looping. */
.tutor-md :deep(.phrase-repeat) {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 4px;
  padding: 0;
  vertical-align: -6px;
  border: 1px solid color-mix(in srgb, var(--al-primary) 35%, transparent);
  border-radius: var(--al-radius-pill, 999px);
  background-color: var(--al-primary-soft);
  color: var(--al-text-link);
  cursor: pointer;
}

.tutor-md :deep(.phrase-repeat)::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 44px;
  height: 44px;
  transform: translate(-50%, -50%);
}

.tutor-md :deep(.phrase-repeat svg) {
  width: 14px;
  height: 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.25;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tutor-md :deep(.phrase-repeat[aria-pressed="true"]) {
  background-color: var(--al-primary-fill);
  border-color: var(--al-primary-fill);
  color: var(--al-white);
}

.tutor-md :deep(.phrase-repeat:focus-visible) {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .tutor-md :deep(.phrase-live:hover) {
    background-color: var(--al-primary-soft);
  }

  .tutor-md :deep(.phrase-repeat[aria-pressed="false"]:hover) {
    background-color: var(--al-primary-soft-hover);
  }
}

/* --- Progressive reveal --- */

/* The reveal masks fully rendered markdown DOM; it never feeds markdown-it a
   growing raw string, which would emit broken markup mid-reveal.
   Geometry: the mask is twice the block's height, so animating its position by
   one block height sweeps the soft edge from the top of the text to the bottom.
   Un-revealed text is dimmed rather than hidden, so the block never reflows and
   the worst case if the animation misbehaves is faded, still-readable text. The
   floor alpha is kept high enough that the dimmed words stay legible for the
   seconds they are masked, rather than dropping under the contrast the design
   system requires. */
.tutor-md-revealing {
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 46%,
    rgba(0, 0, 0, 0.4) 54%,
    rgba(0, 0, 0, 0.4) 100%
  );
  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 46%,
    rgba(0, 0, 0, 0.4) 54%,
    rgba(0, 0, 0, 0.4) 100%
  );
  -webkit-mask-size: 100% 200%;
  mask-size: 100% 200%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  animation: md-reveal var(--al-reveal-duration, 3000ms) linear var(--al-reveal-delay, 0ms)
    forwards;
}

@keyframes md-reveal {
  from {
    -webkit-mask-position: 0% 100%;
    mask-position: 0% 100%;
  }
  to {
    -webkit-mask-position: 0% 0%;
    mask-position: 0% 0%;
  }
}

/* A reveal is motion by definition, so there is nothing to soften: readers who
   ask for reduced motion get the whole reply at once. */
@media (prefers-reduced-motion: reduce) {
  .tutor-md-revealing {
    -webkit-mask-image: none;
    mask-image: none;
    animation: none;
  }
}

/* --- Speaking indicator --- */

/* Shown only while a segment session is delivering this reply. It replaces the
   audio pill for that window, so it occupies the same vertical rhythm. */
.speaking-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  margin-top: 10px;
  padding: 8px 14px;
  border: 1px solid var(--al-border);
  border-radius: 999px;
  background-color: var(--al-surface-raised);
}

.speaking-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.speaking-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--al-primary);
  opacity: 0.45;
}

@media (prefers-reduced-motion: no-preference) {
  .speaking-dot {
    animation: speaking-pulse 1.1s var(--al-ease-move) infinite;
  }

  .speaking-dot:nth-child(2) {
    animation-delay: 0.15s;
  }

  .speaking-dot:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes speaking-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

.speaking-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--al-text-muted);
}

/* --- Disclosures: the spoken version, and an opener's native-language help ---

   Both are the same control revealing a line of text under the message, so they
   share one set of declarations rather than each carrying a near-copy. The chip
   is inline-flex with no nowrap, so a long translated label wraps inside the
   message column instead of widening it at 375px. */

.spoken-text-container,
.opener-help-container {
  margin-top: 10px;
}

.spoken-chip,
.help-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--al-text-muted);
  background: transparent;
  border: 1px solid var(--al-border);
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;
  transition: color var(--al-dur-short) var(--al-ease-move),
    border-color var(--al-dur-short) var(--al-ease-move);
}

.spoken-chip:hover,
.help-chip:hover {
  color: var(--al-text);
  border-color: var(--al-text-muted);
}

.toggle-icon {
  font-size: 0.7rem;
}

.spoken-text-content,
.help-content {
  margin-top: 8px;
  padding: 0.75rem;
  /* Text-relative overlay so the inset box stays visible on the page tint in
     both themes (a fixed surface token melts into the dark surface). */
  background-color: color-mix(in srgb, var(--al-text) 8%, transparent);
  border-radius: 12px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--al-text);
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spoken-text-content,
  .help-content {
    animation: none;
  }
}
</style>
